import React, { RefObject, forwardRef, useEffect, useRef } from 'react';
import Button from 'components/CustomButtons/Button';
import Dialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import Close from '@material-ui/icons/Close';
import styles from 'assets/jss/material-dashboard-pro-react/components/customDialogStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { DescriptionM, DisplayS } from 'components/Typography/Typography';
import { TransitionProps } from '@material-ui/core/transitions';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SlideProps } from '@material-ui/core/Slide/Slide';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { translate } from 'components/Translation';

// @ts-ignore
const useStyles = makeStyles(styles);

const Transition: React.ComponentType<TransitionProps> = forwardRef(function Transition(props, ref) {
	return <Slide direction="down" ref={ref} {...(props as SlideProps)} />;
});

export interface ISideButton {
	text: string | React.ReactElement;
	color: types.theme.buttonColor;
	onClick: () => void;
	disabled?: boolean;
	buttonProps?: object;
	id?: string;
	ref?: boolean;
	hide?: boolean;
}

export interface ICustomDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	header?: string;
	description?: any;
	dialogBody: React.ReactElement;
	className?: string;
	rightSideButtons?: ISideButton[];
	leftSideButtons?: ISideButton[];
	width?: number;
	id?: string;
	loading?: boolean;
	line?: boolean;
	headerWithIcon?: React.ReactElement;
	disableBackdropClick?: boolean;
	setScrollToButton?: Function;
	scrollToButton?: boolean;
	isNewDashBoardEnabled?: boolean;
}

const CustomDialog = ({
	isOpen,
	header,
	description,
	dialogBody,
	hideDialog,
	rightSideButtons,
	leftSideButtons,
	className = '',
	width,
	id,
	loading,
	line = true,
	headerWithIcon,
	disableBackdropClick = false,
	scrollToButton,
	setScrollToButton = () => {},
	isNewDashBoardEnabled,
}: ICustomDialogProps) => {
	const classes = useStyles({ width, isNewDashBoardEnabled });
	const navButtonsRef: RefObject<HTMLDivElement> = useRef(null);
	useEffect(() => {
		if (navButtonsRef.current && scrollToButton) {
			navButtonsRef.current?.scrollIntoView({ behavior: 'smooth' });
			setScrollToButton(false);
		}
	}, [scrollToButton]);

	return (
		<Dialog
			open={isOpen}
			disableAutoFocus={false}
			disableBackdropClick={disableBackdropClick}
			TransitionComponent={Transition}
			keepMounted
			onClose={() => hideDialog()}
			id={id}
			className={classNames({ [classes.dialog]: true, [className]: !!className })}
			BackdropProps={
				isNewDashBoardEnabled
					? {
							style: {
								backgroundColor: 'rgba(5,5,5,1)',
								opacity: 0.65,
							},
					  }
					: {}
			}
		>
			<div className={classes.dialogHeader}>
				{/* keeping the header space even header is empty to adjust cross icon */}
				{isNewDashBoardEnabled ? (
					<DescriptionM customClassName={classes.newThemeHeading}>{header}</DescriptionM>
				) : (
					<DisplayS>{header}</DisplayS>
				)}
				{headerWithIcon ? <>{headerWithIcon}</> : null}
				{description}
				<button className={classes.dialogCloseButton} aria-label="close" onClick={() => hideDialog()}>
					<Close />
				</button>
			</div>
			<div id={!!id ? `${id}-content` : undefined}>{dialogBody}</div>
			<ConditionalRender
				show={!!leftSideButtons || !!rightSideButtons}
				onTrue={() => (
					<div className={classes.buttonsWrapper}>
						<div>
							{leftSideButtons &&
								leftSideButtons.map((button, index) => (
									<Button
										key={index}
										color={button.color}
										onClick={button.onClick}
										disabled={button.disabled}
										{...button.buttonProps}
										id={button.id}
									>
										{button.text}
									</Button>
								))}
						</div>
						<div>
							{rightSideButtons &&
								rightSideButtons.map(
									(button, index) =>
										!button.hide && (
											<Button
												key={index}
												color={button.color}
												onClick={button.onClick}
												disabled={button.disabled}
												{...button.buttonProps}
												ref={button.ref ? navButtonsRef : null}
												id={button.id}
											>
												{button.text}
											</Button>
										)
								)}
						</div>
					</div>
				)}
			/>
			<ConditionalRender
				show={!!loading}
				onTrue={() => (
					<div className={classes.loadingWrapper}>
						<CustomCircularProgress text={translate('loading.it.can.take.a.while')} loading transparent />
					</div>
				)}
			/>
		</Dialog>
	);
};

export default CustomDialog;
